import { Form, Link, useActionData, useLoaderData, useNavigation, useOutletContext } from '@remix-run/react';
import { createBrowserClient } from '@supabase/ssr';
import { type LoaderFunctionArgs, type ActionFunctionArgs, json, redirect } from '@vercel/remix';
import Alert from '~/components/Alert';
import LoadingSpinner from '~/components/Icons/LoadingSpinner';
import Card from '~/components/SimpleCard';
import InputField from '~/components/fields/InputField';
import { LOGIN_TEST_IDS } from '~/constants/testID/login';
import { getSupabase, verifySession } from '~/services/supabase.server';

export const meta = () => [
	{
		title: 'Log into Your Account | PagePilot.ai',
	},
	{
		name: 'description',
		content:
			'Log into your PagePilot.ai account and create high-converting product descriptions and landing pages with AI.',
	},
];

export const action = async ({ request }: ActionFunctionArgs) => {
	const form = await request.formData();
	const email = form.get('email') || '';
	const password = form.get('password') || '';

	if (!email || !password) {
		return json({ error: 'Email and password are required' }, { status: 400 });
	}

	if (password.toString().length < 8) {
		return json({ error: 'Password must be at least 8 characters' }, { status: 400 });
	}

	const { supabase, headers } = getSupabase(request);

	const { error, data } = await supabase.auth.signInWithPassword({
		email: email.toString(),
		password: password.toString(),
	});

	if (error || !data) {
		return json({ error: error ? error.message : 'Something went wrong' }, { status: 400 });
	} else {
		return redirect('/list', {
			headers: headers,
		});
	}
};

export const loader = async ({ request }: LoaderFunctionArgs) => {
	await verifySession(request);

	return {
		env: {
			SUPABASE_URL: process.env.SUPABASE_URL,
			SUPABASE_ANON_KEY: process.env.SUPABASE_ANON_KEY,
		},
	};
};

export default function SigninRoute() {
	const { env } = useLoaderData<any>();
	const data = useActionData();
	const transition = useNavigation();
	const supabase = createBrowserClient(env.SUPABASE_URL, env.SUPABASE_ANON_KEY);

	console.log('env', env);

	const outletContext = useOutletContext();

	console.log('outletContext', outletContext);

	const handleGoogleLogin = async () => {
		await supabase.auth.signInWithOAuth({
			provider: 'google',
			options: {
				redirectTo: `https://pagepilot.ai/auth/callback/google`,
				// redirectTo: `http://localhost:3000/auth/callback/google`,
			},
		});
	};

	return (
		<div className="h-full w-full py-14 lg:mx-0">
			<div className="fixed right-0 top-0 h-full min-h-full w-full bg-[#050520]" />

			<img
				src="/images/landingpage/heroLight.svg"
				className="absolute left-0 right-0 top-0 z-0 mx-auto opacity-60"
				alt="Lights"
			/>

			<div className="w-100% -z-1 absolute left-0 right-0 top-0 max-h-[50vh] min-h-[50vh] overflow-hidden bg-cover bg-no-repeat md:mx-auto">
				<div className="relative mx-auto">
					<div className="rising-stars rising-stars-visible left-0 right-0 mx-auto opacity-60">
						<div className="h-[1px] w-[1px]"></div>
						<div className="h-[1px] w-[1px]"></div>
						<div className="h-[1px] w-[1px]"></div>
					</div>
				</div>
			</div>

			<div className="relative z-10 flex flex-col items-center justify-center px-4 lg:min-h-full">
				<div className="relative w-full">
					<div className="mx-auto grid w-full max-w-[1250px] items-center sm:gap-[30px] lg:grid-cols-2 xl:gap-[100px]">
						<div className="relative">
							<img
								src="/images/landingpage/heroSphere.webp"
								className="animate-smoothMove absolute -left-[370px] top-[125px] z-0 hidden  max-w-[600px] blur-sm  2xl:flex"
								alt="Decoration"
							/>

							<Link to={'/'}>
								<div data-testid={LOGIN_TEST_IDS.LOGO} className="mb-10 flex justify-center">
									<div className="linear mr-4  flex h-12 w-12 flex-col items-center justify-end rounded-xl bg-gradient-to-br from-brandLinear to-blueSecondary">
										<svg className="h-10 w-10" xmlns="http://www.w3.org/2000/svg" id="_x32_" viewBox="0 0 512 512">
											<path
												fill="#ffffff"
												d="m154.219 205.188 12 129.843-55.297 70.532-14.516-109zM357.781 205.188l-12.015 129.843 55.297 70.532 14.531-109zM323.813 62.219C305.125 23.328 275.656 0 256 0s-49.125 23.344-67.813 62.219h135.626zM331.328 80.469H180.672c-6.641 19.156-10.594 41.094-10.047 65.188 1.609 69.063 18.047 190.984 18.047 190.984l122.641-.016 12 .016s16.453-121.938 18.031-190.984c.578-24.094-3.375-46.048-10.016-65.188zM256 231.031c-23.578 0-42.688-19.094-42.688-42.672s19.125-42.672 42.688-42.672 42.672 19.094 42.656 42.672c.016 23.579-19.093 42.672-42.656 42.672zM429.516 512c-6.109-17.594-22.859-30.219-42.531-30.219a44.962 44.962 0 0 0-17.766 3.625c-1.875-20.578-19.188-36.688-40.25-36.688-3.047 0-6 .328-8.844.969.031-.563.031-1.125.031-1.688 0-15.531-12.594-28.125-28.125-28.125-4.031 0-7.875.844-11.344 2.375-9.734-17.219-12.563-62.156-12.563-62.156h-25.5s-3.266 49.344-14.813 65.688a23.206 23.206 0 0 0-8.75-1.688c-13.016 0-23.563 10.547-23.563 23.563 0 .984.063 1.953.188 2.906-3.719-1.203-3.281-1.844-7.406-1.844-16.484 0-30.531 10.406-35.938 25a50.955 50.955 0 0 0-19.625-3.906c-25.156 0-46.063 18.219-50.234 42.188h347.033z"
												className="st0"></path>
										</svg>
									</div>
									<span className="self-center whitespace-nowrap text-lg font-semibold text-white sm:text-2xl">
										PAGE
									</span>
									<span className="self-center whitespace-nowrap text-lg text-white sm:text-2xl">PILOT</span>
								</div>
							</Link>
							<Card extra="flex flex-col w-full max-w-[600px] mx-auto my-auto p-8">
								<h1
									data-testid={LOGIN_TEST_IDS.INFO_TEXT}
									className="mx-auto max-w-[320px] text-center font-jakarta text-xl font-bold text-blueSecondary sm:text-3xl lg:max-w-[400px]">
									Launch and find winning products 20X faster
								</h1>
								<h3
									data-testid={LOGIN_TEST_IDS.TITLE}
									className="mt-4 text-center font-inter text-lg font-medium text-black">
									Login into dashboard
								</h3>

								{data?.error && (
									<Alert showTitle={false} isError={true} extra="mt-4">
										{data.error}
										{data.error == 'Email not confirmed' && (
											<Link to="/verify-email" className="font-medium">
												{' '}
												- <span className="underline">Click here to verify it</span>
											</Link>
										)}
									</Alert>
								)}

								<button
									data-testid={LOGIN_TEST_IDS.SIGN_IN_GOOGLE_BUTTON}
									onClick={handleGoogleLogin}
									className="text-dark mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-gray-DEFAULT-100 py-3 font-medium text-gray-DEFAULT-800 hover:bg-gray-200"
									type="submit">
									<div className="googleIcon">
										<svg className="h-5 w-5" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
											{' '}
											<g clipPath="url(#clip0_2_53)">
												{' '}
												<path
													d="M19.7874 10.225C19.7874 9.56668 19.7291 8.94168 19.6291 8.33334H10.2124V12.0917H15.6041C15.3624 13.325 14.6541 14.3667 13.6041 15.075V17.575H16.8207C18.7041 15.8333 19.7874 13.2667 19.7874 10.225Z"
													fill="#4285F4"
												/>{' '}
												<path
													d="M10.2124 20C12.9124 20 15.1707 19.1 16.8207 17.575L13.6041 15.075C12.7041 15.675 11.5624 16.0417 10.2124 16.0417C7.60408 16.0417 5.39575 14.2833 4.60408 11.9083H1.28741V14.4833C2.92908 17.75 6.30408 20 10.2124 20Z"
													fill="#01B574"
												/>{' '}
												<path
													d="M4.60407 11.9083C4.39574 11.3083 4.2874 10.6667 4.2874 9.99999C4.2874 9.33333 4.40407 8.69166 4.60407 8.09166V5.51666H1.2874C0.604068 6.86666 0.212402 8.38333 0.212402 9.99999C0.212402 11.6167 0.604068 13.1333 1.2874 14.4833L4.60407 11.9083Z"
													fill="#FBBC05"
												/>{' '}
												<path
													d="M10.2124 3.95833C11.6874 3.95833 13.0041 4.46667 14.0457 5.45834L16.8957 2.60833C15.1707 0.991667 12.9124 0 10.2124 0C6.30408 0 2.92908 2.25 1.28741 5.51667L4.60408 8.09167C5.39575 5.71667 7.60408 3.95833 10.2124 3.95833Z"
													fill="#EA4335"
												/>{' '}
											</g>{' '}
											<defs>
												{' '}
												<clipPath id="clip0_2_53">
													{' '}
													<rect width="20" height="20" fill="white" />{' '}
												</clipPath>{' '}
											</defs>{' '}
										</svg>
									</div>
									Login with Google
								</button>

								<div className="mt-6 h-[1px] w-full bg-slate-100" />

								{/* user info */}
								<Form method="post" className="mt-4">
									{/* Email */}
									<InputField
										dataTestIdInput={LOGIN_TEST_IDS.EMAIL_INPUT}
										variant="auth"
										inputExtra="placeholder-black/50 text-black !mb-2"
										label=""
										placeholder="Email"
										id="email"
										type="email"
										name="email"
										required
									/>

									<InputField
										dataTestIdInput={LOGIN_TEST_IDS.PASSWORD_INPUT}
										variant="auth"
										inputExtra="placeholder-black/50 text-black !mb-2"
										label=""
										placeholder="Password"
										id="password"
										type="password"
										name="password"
										required
										autoComplete="current-password"
									/>

									<div
										data-testid={LOGIN_TEST_IDS.FORGOT_PASSWORD_BUTTON}
										className="mt-4 flex items-center justify-between">
										<Link
											to={'/forgot-password'}
											className="mb-2 mt-2 text-sm font-medium text-brand-500 hover:text-brand-500">
											Forgot password?
										</Link>
									</div>

									<button
										data-testid={LOGIN_TEST_IDS.SIGN_IN_BUTTON}
										disabled={transition.state !== 'idle'}
										className={`btn-primary mt-4 w-full py-3 text-sm ${transition.state !== 'idle' ? 'animate-pulse' : ''}`}>
										{transition.state !== 'idle' ? (
											<>
												<LoadingSpinner /> Loading...
											</>
										) : (
											'Login'
										)}
									</button>
								</Form>

								<div className="mt-6 flex flex-col items-center justify-center">
									<div className="flex flex-row items-center gap-2">
										<div className="flex">
											<img
												className="h-9 w-9 rounded-full border-2 border-white"
												src={'/images/landingpage/andrewd.webp'}
												alt="PagePilot User"
											/>
											<img
												className="-ml-4 h-9 w-9 rounded-full border-2 border-white"
												src={'/images/landingpage/benp.webp'}
												alt="PagePilot User"
											/>
											<img
												className="-ml-4 h-9 w-9 rounded-full border-2 border-white"
												src={'/images/landingpage/luisr.webp'}
												alt="PagePilot User"
											/>
											<img
												className="-ml-4 h-9 w-9 rounded-full border-2 border-white"
												src={'/images/landingpage/mac.webp'}
												alt="PagePilot User"
											/>
										</div>
										<div className="flex">
											<svg
												xmlns="http://www.w3.org/2000/svg"
												viewBox="0 0 20 20"
												fill="currentColor"
												className="h-4 w-4 text-yellow-400">
												<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
											</svg>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												viewBox="0 0 20 20"
												fill="currentColor"
												className="h-4 w-4 text-yellow-400">
												<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
											</svg>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												viewBox="0 0 20 20"
												fill="currentColor"
												className="h-4 w-4 text-yellow-400">
												<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
											</svg>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												viewBox="0 0 20 20"
												fill="currentColor"
												className="h-4 w-4 text-yellow-400">
												<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
											</svg>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												viewBox="0 0 20 20"
												fill="currentColor"
												className="h-4 w-4 text-yellow-400">
												<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
											</svg>
										</div>
									</div>
									<p className="mt-1 text-center text-xs font-medium text-black">
										Used by more than 2300+ dropshippers daily.
									</p>
								</div>

								<div className="mt-6 border-t border-t-black/10 pt-6">
									<span className="text-sm font-medium text-bluePrimary">Not registered yet?</span>
									<Link
										data-testid={LOGIN_TEST_IDS.CREATE_NEW_ACCOUNT_BUTTON}
										to={'/signup'}
										className="ml-1 text-sm font-medium text-brand-500 hover:text-brand-500">
										Create an Account
									</Link>
								</div>
							</Card>
						</div>
						<div>
							<div className="mt-10 flex flex-col items-center justify-center gap-5 sm:mt-0">
								<svg className="h-8 w-8 fill-slate-400" viewBox="0 0 33 27" xmlns="http://www.w3.org/2000/svg">
									<path
										fillRule="evenodd"
										clipRule="evenodd"
										d="M33 4.21692C28.0573 6.55966 25.586 9.31235 25.586 12.4751C27.6928 12.7093 29.4348 13.539 30.8123 14.9642C32.1897 16.3894 32.8785 18.039 32.8785 19.9132C32.8785 21.9046 32.21 23.5835 30.873 24.9501C29.5361 26.3167 27.8548 27 25.8291 27C23.5604 27 21.5955 26.1117 19.9344 24.3351C18.2734 22.5586 17.4429 20.4013 17.4429 17.8633C17.4429 10.2494 21.8588 4.29503 30.6907 0L33 4.21692ZM15.5571 4.21692C10.574 6.55966 8.08242 9.31235 8.08242 12.4751C10.2296 12.7093 11.992 13.539 13.3694 14.9642C14.7469 16.3894 15.4356 18.039 15.4356 19.9132C15.4356 21.9046 14.757 23.5835 13.3998 24.9501C12.0426 26.3167 10.3512 27 8.3255 27C6.05674 27 4.10199 26.1117 2.46119 24.3351C0.820388 22.5586 0 20.4013 0 17.8633C0 10.2494 4.39566 4.29503 13.1871 0L15.5571 4.21692Z"
									/>
								</svg>

								<p className="text-center text-base font-light leading-9 text-slate-400">
									"PagePilot.ai is a must-have for all ecom store owners! The AI product pages look more branded than
									some 8-figure stores. There's no reason not to be taking advantage of this tool"
								</p>

								<div className="flex items-center gap-6">
									<img
										className="max-w-[50px] rounded-full"
										src="/images/landingpage/reviews/anthonyeclipse.webp"
										alt="Anthony Eclipse"
									/>
									<div className="flex flex-col gap-1">
										<div className="text-base font-medium text-white">Anthony Eclipse</div>
										<div className="flex items-center">
											<svg
												xmlns="http://www.w3.org/2000/svg"
												viewBox="0 0 20 20"
												fill="currentColor"
												className="h-4 w-4 text-yellow-400">
												<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
											</svg>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												viewBox="0 0 20 20"
												fill="currentColor"
												className="h-4 w-4 text-yellow-400">
												<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
											</svg>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												viewBox="0 0 20 20"
												fill="currentColor"
												className="h-4 w-4 text-yellow-400">
												<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
											</svg>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												viewBox="0 0 20 20"
												fill="currentColor"
												className="h-4 w-4 text-yellow-400">
												<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
											</svg>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												viewBox="0 0 20 20"
												fill="currentColor"
												className="h-4 w-4 text-yellow-400">
												<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
											</svg>
										</div>
									</div>
								</div>
							</div>

							<div className="mt-10 border-t border-t-white/10 py-10">
								<h3 className="text-transparent mb-6 bg-gradient-to-b from-white from-40% to-[#b2b2b2] bg-clip-text text-center text-xl font-medium">
									We help you launch & scale winning products by offering
								</h3>
								<div className="mx-auto grid w-full grid-cols-2 items-center justify-center gap-x-4 gap-y-6 sm:grid-cols-4">
									<div className="card-bg mx-auto flex w-full flex-col gap-2 text-center font-medium">
										<div className="inner flex flex-col items-center justify-center gap-4 px-2 py-5 text-sm leading-4 text-slate-400">
											<img src="/images/emoji/sparkles.svg" className="w-[20px]" alt="Page Pilot Benefit" />
											<span>
												AI Product <br /> Pages
											</span>
										</div>
									</div>

									<div className="card-bg mx-auto flex w-full flex-col gap-2 text-center font-medium">
										<div className="inner flex flex-col items-center justify-center gap-4 px-2 py-5 text-sm leading-4 text-slate-400">
											<img src="/images/shopifyIcon.svg" className="w-[20px]" alt="Page Pilot Benefit" />
											<span>
												Shopify <br /> Integration
											</span>
										</div>
									</div>

									<div className="card-bg mx-auto flex w-full flex-col gap-2 text-center font-medium">
										<div className="inner flex flex-col items-center justify-center gap-4 px-2 py-5 text-sm leading-4 text-slate-400">
											<img src="/images/emoji/writingHand.svg" className="w-[20px]" alt="Page Pilot Benefit" />
											<span>
												Ad <br /> Copies
											</span>
										</div>
									</div>

									<div className="card-bg mx-auto flex w-full flex-col gap-2 text-center font-medium">
										<div className="inner flex flex-col items-center justify-center gap-4 px-2 py-5 text-sm leading-4 text-slate-400">
											<img src="/images/emoji/trophy.svg" className="w-[20px]" alt="Page Pilot Benefit" />
											<span>
												Winning <br /> Products
											</span>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
